import React, { useEffect, useMemo, useState } from "react";
import { motion } from "motion/react";
import { moduleApi } from "../../lib/api";
import { Plus, Archive, BarChart3, Search, UtensilsCrossed, ArrowRight, Zap } from "lucide-react";
import { formatEuro, mapReservationFromApi, type Board, type Reservation } from "./types";
import { NewBoardModal } from "./NewBoardModal";
import { AutomationRequestModal } from "./AutomationRequestModal";

export function Dashboard({
  boards,
  onOpenBoard,
  onOpenArchives,
  onArchive,
  onCreateBoard,
  onOpenGlobal,
  onDeleteBoard,
}: {
  boards: Board[];
  onOpenBoard: (id: number) => void;
  onOpenArchives: () => void;
  onArchive: (id: number) => void;
  onCreateBoard: (title: string) => void;
  onOpenGlobal: () => void;
  onDeleteBoard: (id: number) => void;
}) {
  const [isNewBoardModalOpen, setIsNewBoardModalOpen] = useState(false);
  const [isAutomationModalOpen, setIsAutomationModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const activeBoards = useMemo(() => boards.filter((b) => !b.isArchived), [boards]);
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredBoards = normalizedSearch
    ? activeBoards.filter((b) => `${b.title} ${b.createdAt}`.toLowerCase().includes(normalizedSearch))
    : activeBoards;

  useEffect(() => {
    (async () => {
      const allReservations: Reservation[] = [];
      for (const board of activeBoards) {
        try {
          const data = await moduleApi.getExceptionServiceBoardReservations(board.id);
          if (Array.isArray(data)) allReservations.push(...data.map((r: any) => mapReservationFromApi(r)));
        } catch (e) {
          console.error(`Failed to load reservations for board ${board.id}`, e);
        }
      }
      setReservations(allReservations);
    })();
  }, [activeBoards]);

  const boardStats = useMemo(() => {
    const statsMap = new Map<number, { totalCovers: number; totalEncaisse: number; totalTheoretical: number; totalReste: number }>();
    activeBoards.forEach((board) => {
      const boardReservations = reservations.filter((r) => r.boardId === board.id);
      let totalCovers = 0, totalEncaisse = 0, totalTheoretical = 0;
      boardReservations.forEach((r) => {
        totalCovers += r.adults + r.children;
        totalTheoretical += r.adults * r.tarifAdult + r.children * r.tarifChild;
        totalEncaisse += r.cb + r.amex + r.cash + r.cheque + r.zen + r.virm;
      });
      statsMap.set(board.id, { totalCovers, totalEncaisse, totalTheoretical, totalReste: totalTheoretical - totalEncaisse });
    });
    return statsMap;
  }, [reservations, activeBoards]);

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-black text-content flex items-center gap-2">
            <UtensilsCrossed size={22} className="text-brand-500" /> Service d'exception
          </h1>
          <p className="text-sm text-content-muted mt-1">Gérez vos tableaux de réservations et suivez le chiffre d'affaires en temps réel.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onOpenArchives} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface-soft hover:bg-surface border border-border text-content-muted hover:text-content text-sm font-bold transition-all">
            <Archive size={15} /> Archives
          </button>
          <button onClick={onOpenGlobal} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface-soft hover:bg-surface border border-border text-content-muted hover:text-content text-sm font-bold transition-all">
            <BarChart3 size={15} /> Vue globale
          </button>
          <button onClick={() => setIsAutomationModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-surface-soft hover:bg-surface border border-border text-content-muted hover:text-content text-sm font-bold transition-all">
            <Zap size={15} /> Saisir les réservations automatiquement
          </button>
          <button onClick={() => setIsNewBoardModalOpen(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold shadow-lg shadow-brand-500/20 transition-all">
            <Plus size={16} /> Nouveau tableau
          </button>
        </div>
      </header>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-content-muted" />
        <input
          type="text"
          placeholder="Rechercher un tableau..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-surface border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-content outline-none focus:border-brand-500 transition-colors"
        />
      </div>

      {filteredBoards.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-3xl bg-surface/40">
          <UtensilsCrossed size={40} className="mx-auto mb-3 text-content-muted opacity-40" />
          <p className="text-content-muted font-medium">
            {searchTerm ? "Aucun tableau ne correspond à votre recherche" : "Aucun tableau actif — créez-en un pour commencer."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filteredBoards.map((board) => {
            const s = boardStats.get(board.id);
            return (
              <motion.div
                key={board.id}
                layout
                className="bg-surface border border-border rounded-2xl p-6 flex flex-col shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-start justify-between mb-1">
                  <h2 className="text-lg font-bold text-content">{board.title}</h2>
                </div>
                <p className="text-xs text-content-muted mb-5">Créé le {board.createdAt}</p>

                <div className="grid grid-cols-2 gap-3 mb-5 text-sm">
                  <div className="bg-surface-soft rounded-xl p-3">
                    <div className="text-[11px] text-content-muted uppercase tracking-wide font-bold mb-0.5">Couverts</div>
                    <div className="text-content font-bold">{s?.totalCovers || 0}</div>
                  </div>
                  <div className="bg-surface-soft rounded-xl p-3">
                    <div className="text-[11px] text-content-muted uppercase tracking-wide font-bold mb-0.5">Encaissé</div>
                    <div className="text-emerald-500 font-bold">{formatEuro(s?.totalEncaisse || 0)}</div>
                  </div>
                  <div className="bg-surface-soft rounded-xl p-3">
                    <div className="text-[11px] text-content-muted uppercase tracking-wide font-bold mb-0.5">CA théorique</div>
                    <div className="text-content font-bold">{formatEuro(s?.totalTheoretical || 0)}</div>
                  </div>
                  <div className="bg-surface-soft rounded-xl p-3">
                    <div className="text-[11px] text-content-muted uppercase tracking-wide font-bold mb-0.5">Reste à payer</div>
                    <div className="text-warning-500 font-bold">{formatEuro(s?.totalReste || 0)}</div>
                  </div>
                </div>

                <div className="mt-auto flex items-center justify-between">
                  <button
                    onClick={() => onOpenBoard(board.id)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold transition-all"
                  >
                    Ouvrir <ArrowRight size={14} />
                  </button>
                  <div className="flex items-center gap-4 text-xs font-bold">
                    <button onClick={() => onArchive(board.id)} className="text-content-muted hover:text-content transition-colors">Archiver</button>
                    <button onClick={() => onDeleteBoard(board.id)} className="text-red-400 hover:text-red-300 transition-colors">Supprimer</button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {isNewBoardModalOpen && (
        <NewBoardModal onClose={() => setIsNewBoardModalOpen(false)} onCreate={onCreateBoard} />
      )}
      {isAutomationModalOpen && (
        <AutomationRequestModal onClose={() => setIsAutomationModalOpen(false)} />
      )}
    </div>
  );
}
